import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, MapPin, Loader2, Clock, CalendarDays, 
  X, Plus, Shield, AlertCircle
} from "lucide-react";
import { Office, OfficeFormData, DEFAULT_OFFICE_ATTENDANCE, DEFAULT_OFFICE_HOLIDAYS, OfficeAttendanceSettings, OfficeHolidayConfig } from "@/types/office";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { cn } from "@/lib/utils";

interface OfficeFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  office?: Office | null;
  onSave: (data: OfficeFormData) => Promise<void>;
}

const WEEKDAY_OPTIONS = [
  { value: "sunday", label: "Sunday" },
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
];

export function OfficeFormSheet({
  open,
  onOpenChange,
  office,
  onSave,
}: OfficeFormSheetProps) {
  const [formData, setFormData] = useState<OfficeFormData>({
    name: "",
    city: "",
    address: "",
    latitude: 0,
    longitude: 0,
    attendanceSettings: { ...DEFAULT_OFFICE_ATTENDANCE },
    holidayConfig: { ...DEFAULT_OFFICE_HOLIDAYS },
  });
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [newHolidayDate, setNewHolidayDate] = useState("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Populate form when editing
  useEffect(() => {
    if (office) {
      setFormData({
        name: office.name,
        city: office.city,
        address: office.address,
        latitude: office.geoLocation.latitude,
        longitude: office.geoLocation.longitude,
        attendanceSettings: office.attendanceSettings ?? { ...DEFAULT_OFFICE_ATTENDANCE },
        holidayConfig: office.holidayConfig ?? { ...DEFAULT_OFFICE_HOLIDAYS },
      });
    } else {
      setFormData({
        name: "",
        city: "",
        address: "",
        latitude: 0,
        longitude: 0,
        attendanceSettings: { ...DEFAULT_OFFICE_ATTENDANCE },
        holidayConfig: { ...DEFAULT_OFFICE_HOLIDAYS },
      });
    }
    setNewHolidayDate("");
    setValidationErrors({});
  }, [office, open]);

  const updateAttendance = (updates: Partial<OfficeAttendanceSettings>) => {
    setFormData(prev => ({
      ...prev,
      attendanceSettings: { ...prev.attendanceSettings, ...updates },
    }));
  };

  const updateHolidays = (updates: Partial<OfficeHolidayConfig>) => {
    setFormData(prev => ({
      ...prev,
      holidayConfig: { ...prev.holidayConfig, ...updates },
    }));
  };

  const addHolidayDate = () => {
    if (!newHolidayDate) return;
    const todayStr = new Date().toISOString().split('T')[0];
    if (newHolidayDate <= todayStr) {
      toast.error("Holiday date must be in the future (from tomorrow)");
      return;
    }
    const existing = formData.holidayConfig.holidayDates || [];
    if (existing.includes(newHolidayDate)) {
      toast.error("Date already added");
      return;
    }
    updateHolidays({ holidayDates: [...existing, newHolidayDate].sort() });
    setNewHolidayDate("");
  };

  const removeHolidayDate = (date: string) => {
    updateHolidays({
      holidayDates: (formData.holidayConfig.holidayDates || []).filter(d => d !== date),
    });
  };

  const handleGetCurrentLocation = () => {
    setGettingLocation(true);
    
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }));
        toast.success("Location captured successfully!");
        setGettingLocation(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        toast.error("Failed to get current location. Please enable location services.");
        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = "Office name is required";
    if (!formData.city.trim()) errors.city = "City is required";
    if (!formData.address.trim()) errors.address = "Address is required";
    if (formData.latitude === 0 && formData.longitude === 0) errors.location = "Please set the office location";
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the validation errors");
      // Scroll to first error
      const firstField = Object.keys(errors)[0];
      const el = document.querySelector(`[name="${firstField}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await onSave(formData);
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving office:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatHolidayLabel = (dateStr: string) => {
    try {
      const d = parse(dateStr, 'yyyy-MM-dd', new Date());
      return format(d, 'dd MMM yyyy (EEE)');
    } catch {
      return dateStr;
    }
  };

  const clearError = (field: string) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const formContent = (
    <div className="space-y-5 pb-8">
      {/* SECTION 1: BASIC INFORMATION */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 pb-1">
          <Building2 className="h-4 w-4 text-green-600" />
          <h3 className="font-semibold text-green-800">Basic Information</h3>
        </div>
        
        <div className="space-y-2">
          <Label className="text-sm font-medium">Office Name *</Label>
          <Input
            name="name"
            placeholder="Ex - Noida Office"
            value={formData.name}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, name: e.target.value }));
              clearError("name");
            }}
            className={cn(
              "h-12 placeholder:text-muted-foreground/50",
              validationErrors.name && "border-red-500 focus-visible:ring-red-500"
            )}
          />
          {validationErrors.name && (
            <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
              <AlertCircle className="h-3 w-3" />
              <span>{validationErrors.name}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-sm font-medium">City *</Label>
            <Input
              name="city"
              placeholder="Ex - Noida"
              value={formData.city}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, city: e.target.value }));
                clearError("city");
              }}
              className={cn(
                "h-12 placeholder:text-muted-foreground/50",
                validationErrors.city && "border-red-500 focus-visible:ring-red-500"
              )}
            />
            {validationErrors.city && (
              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                <AlertCircle className="h-3 w-3" />
                <span>{validationErrors.city}</span>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Address *</Label>
            <Input
              name="address"
              placeholder="Full address"
              value={formData.address}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, address: e.target.value }));
                clearError("address");
              }}
              className={cn(
                "h-12 placeholder:text-muted-foreground/50",
                validationErrors.address && "border-red-500 focus-visible:ring-red-500"
              )}
            />
            {validationErrors.address && (
              <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                <AlertCircle className="h-3 w-3" />
                <span>{validationErrors.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SECTION 2: LOCATION */}
      <div className="space-y-3 pt-4 border-t">
        <div className="flex items-center justify-between pb-1">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-green-600" />
            <h3 className="font-semibold text-green-800">Office Location</h3>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleGetCurrentLocation}
            disabled={gettingLocation}
            className="gap-1.5 h-9 text-xs border-green-300 text-green-700 hover:bg-green-50"
          >
            {gettingLocation ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Getting...</>
            ) : (
              <><MapPin className="h-3.5 w-3.5" /> Get Current Location</>
            )}
          </Button>
        </div>

        {validationErrors.location && (
          <div className="flex items-center gap-1 text-red-600 text-xs">
            <AlertCircle className="h-3 w-3" />
            <span>{validationErrors.location}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Latitude</Label>
            <Input
              type="number"
              step="any"
              placeholder="28.6139"
              value={formData.latitude || ""}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, latitude: parseFloat(e.target.value) || 0 }));
                clearError("location");
              }}
              className="h-12 placeholder:text-muted-foreground/50"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Longitude</Label>
            <Input
              type="number"
              step="any"
              placeholder="77.2090"
              value={formData.longitude || ""}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, longitude: parseFloat(e.target.value) || 0 }));
                clearError("location");
              }}
              className="h-12 placeholder:text-muted-foreground/50"
            />
          </div>
        </div>

        {formData.latitude !== 0 && formData.longitude !== 0 && (
          <a
            href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
          >
            <MapPin className="h-3 w-3" /> View on Google Maps
          </a>
        )}
      </div>

      {/* SECTION 3: ATTENDANCE SETTINGS */}
      <div className="space-y-3 pt-4 border-t">
        <div className="flex items-center gap-2 pb-1">
          <Clock className="h-4 w-4 text-green-600" />
          <h3 className="font-semibold text-green-800">Attendance Settings</h3>
        </div>

        <div className="p-4 border-2 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Shift Start</Label>
              <Input
                type="time"
                value={formData.attendanceSettings.shiftStartTime}
                onChange={(e) => updateAttendance({ shiftStartTime: e.target.value })}
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Shift End</Label>
              <Input
                type="time"
                value={formData.attendanceSettings.shiftEndTime}
                onChange={(e) => updateAttendance({ shiftEndTime: e.target.value })}
                className="h-12"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Radius (m)</Label>
              <Input
                type="number"
                min={50}
                max={5000}
                step={50}
                placeholder="200"
                value={formData.attendanceSettings.officeRadius}
                onChange={(e) => updateAttendance({ officeRadius: parseInt(e.target.value) || 200 })}
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Grace (min)</Label>
              <Input
                type="number"
                min={0}
                max={60}
                placeholder="0"
                value={formData.attendanceSettings.gracePeriodMinutes}
                onChange={(e) => updateAttendance({ gracePeriodMinutes: parseInt(e.target.value) || 0 })}
                className="h-12"
              />
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 4: HOLIDAY CONFIGURATION */}
      <div className="space-y-3 pt-4 border-t">
        <div className="flex items-center justify-between pb-1">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-green-600" />
            <h3 className="font-semibold text-green-800">Holiday Configuration</h3>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="holiday-enabled-sheet" className="text-xs text-muted-foreground">Enable</Label>
            <Switch
              id="holiday-enabled-sheet"
              checked={formData.holidayConfig.enabled}
              onCheckedChange={(v) => updateHolidays({ enabled: v })}
            />
          </div>
        </div>

        {formData.holidayConfig.enabled && (
          <div className="p-4 border-2 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200 space-y-3">
            {/* Weekly Off */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Weekly Off Day</Label>
              <Select
                value={formData.holidayConfig.weeklyOff}
                onValueChange={(v) => updateHolidays({ weeklyOff: v })}
              >
                <SelectTrigger className="h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAY_OPTIONS.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Saturday toggles */}
            <div className="grid grid-cols-2 gap-3">
              <div 
                className="flex items-center space-x-2 p-3 border-2 rounded-xl cursor-pointer transition-all hover:bg-muted/50"
                style={{
                  backgroundColor: formData.holidayConfig.secondSaturdayOff ? 'rgb(254 252 232)' : 'transparent',
                  borderColor: formData.holidayConfig.secondSaturdayOff ? 'rgb(251 191 36)' : 'rgb(226 232 240)'
                }}
                onClick={() => updateHolidays({ secondSaturdayOff: !formData.holidayConfig.secondSaturdayOff })}
              >
                <Switch
                  checked={formData.holidayConfig.secondSaturdayOff}
                  onCheckedChange={(v) => updateHolidays({ secondSaturdayOff: v })}
                />
                <div className="flex-1">
                  <Label className="text-xs font-semibold cursor-pointer block">2nd Sat Off</Label>
                </div>
              </div>
              
              <div 
                className="flex items-center space-x-2 p-3 border-2 rounded-xl cursor-pointer transition-all hover:bg-muted/50"
                style={{
                  backgroundColor: formData.holidayConfig.fourthSaturdayOff ? 'rgb(240 253 244)' : 'transparent',
                  borderColor: formData.holidayConfig.fourthSaturdayOff ? 'rgb(34 197 94)' : 'rgb(226 232 240)'
                }}
                onClick={() => updateHolidays({ fourthSaturdayOff: !formData.holidayConfig.fourthSaturdayOff })}
              >
                <Switch
                  checked={formData.holidayConfig.fourthSaturdayOff}
                  onCheckedChange={(v) => updateHolidays({ fourthSaturdayOff: v })}
                />
                <div className="flex-1">
                  <Label className="text-xs font-semibold cursor-pointer block">4th Sat Off</Label>
                </div>
              </div>
            </div>

            {/* Specific Holiday Dates */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Specific Holidays</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={newHolidayDate}
                  onChange={(e) => setNewHolidayDate(e.target.value)}
                  min={(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })()}
                  className="h-12 flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addHolidayDate}
                  disabled={!newHolidayDate}
                  className="h-12 gap-1 px-4"
                >
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>

              {(formData.holidayConfig.holidayDates?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {formData.holidayConfig.holidayDates.map(date => (
                    <Badge
                      key={date}
                      variant="secondary"
                      className="text-xs gap-1 pr-1"
                    >
                      {formatHolidayLabel(date)}
                      <button
                        type="button"
                        onClick={() => removeHolidayDate(date)}
                        className="ml-0.5 hover:text-red-600 rounded-full"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <Button 
        onClick={handleSubmit} 
        disabled={loading} 
        className="w-full h-12 text-base font-semibold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg gap-2"
      >
        {loading ? (
          <><Loader2 className="h-5 w-5 animate-spin" /> Saving...</>
        ) : (
          <>{office ? "Update Office" : "Create Office"}</>
        )}
      </Button>
    </div>
  );

  // Mobile: Bottom Sheet, Desktop: Dialog
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <>
      {/* Mobile: Sheet */}
      <Sheet open={open && isMobile} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl flex flex-col">
          <SheetHeader className="pb-4 px-4 flex-shrink-0">
            <SheetTitle className="text-xl font-bold">
              {office ? "Edit Office" : "Add New Office"}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4">
            {formContent}
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop: Dialog */}
      <Dialog open={open && !isMobile} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl pr-6">
              {office ? "Edit Office" : "Add New Office"}
            </DialogTitle>
          </DialogHeader>
          {formContent}
        </DialogContent>
      </Dialog>
    </>
  );
}
